import dayjs from 'dayjs';
import { t } from 'i18next';
import { UseFormReset } from 'react-hook-form';

import { addressService } from 'addresses/services';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider } from 'core/states';
import { CorporateUserStatus } from 'corporate/enums';
import { corporateUserService } from 'corporate/services';
import { useJobActions } from 'jobs/hooks';
import { jobService } from 'jobs/services';
import { IJob, IUpdateJobForm } from 'jobs/types';

import { mapUpdateJobForm } from './mappers';
import { updateJobSchema } from './schema';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  jobs: IJob[];
  onSubmitSuccess?: () => void;
}

export const UpdateJobs = ({ isOpen, onClose, jobs, onSubmitSuccess }: IProps) => {
  const { upsertJobs } = useJobActions();

  const onSubmit = (reset: UseFormReset<IUpdateJobForm>) => async (data: IUpdateJobForm) => {
    const { success } = await upsertJobs(mapUpdateJobForm(data, jobs));
    if (!success) {
      return;
    }

    setTimeout(() => {
      onSubmitSuccess?.();

      handleOnClose(reset);
    }, 1000);
  };

  const handleOnClose = (reset: UseFormReset<IUpdateJobForm>) => {
    reset();
    onClose();
  };

  return (
    <FormProvider schema={updateJobSchema()} resetDefaultValues>
      <Form
        isOpen={isOpen}
        onClose={() => onClose()}
        title={t('titleUpdateSelected')}
        onSubmit={onSubmit}
      >
        <FormField
          name="status"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelStatus'),
            getOptions: jobService.getJobStatuses,
          }}
        />

        <FormField
          name="externalLocationRegion"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelTheatre'),
          }}
        />

        <FormField
          name="externalLocationCountry"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelExternalLocationCountry'),
            getOptions: addressService.getCountriesAutocomplete,
          }}
        />

        <FormField
          name="customerNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelCustomerNumber'),
          }}
        />

        <FormField
          name="requestedByUser"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelRequestedByUser'),
            getOptions: corporateUserService.getCorporateUsersAutocomplete,
            requestPayload: {
              filters: {
                status: CorporateUserStatus.ACTIVE,
              },
            },
          }}
        />

        <FormField
          name="requestedByUserEmployeeId"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelRequestedByUserEmployeeId'),
          }}
        />

        <FormField
          name="supportUser"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelSupportUser'),
            getOptions: corporateUserService.getCorporateUsersAutocomplete,
            requestPayload: {
              filters: {
                status: CorporateUserStatus.ACTIVE,
              },
            },
          }}
        />

        <FormField
          name="supportCompletedDate"
          type={InputType.DATE_INPUT}
          inputProps={{
            label: t('labelSupportCompletedDate'),
            minDate: dayjs(),
          }}
        />
      </Form>
    </FormProvider>
  );
};
